import { useState, useMemo } from 'react';
import { secsData } from '../utils/secsDataLoader';
import DataTypeCard from '../components/DataTypeCard';

export default function DataTypes() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = {
    all: 'All Types',
    structure: 'Structure',
    text: 'Text',
    binary: 'Binary',
    unsigned: 'Unsigned Integer',
    signed: 'Signed Integer',
    float: 'Floating Point',
  };

  const getCategory = (code: string): string => {
    if (code === 'L') return 'structure';
    if (code === 'A') return 'text';
    if (code === 'B' || code === 'BOOLEAN') return 'binary';
    if (code.startsWith('U')) return 'unsigned';
    if (code.startsWith('I')) return 'signed';
    if (code.startsWith('F')) return 'float';
    return 'other';
  };

  const filteredDataTypes = useMemo(() => {
    return Object.entries(secsData.dataTypes).filter(([code, dataType]) => {
      const matchesSearch = searchQuery === '' ||
        code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dataType.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dataType.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = selectedCategory === 'all' ||
        getCategory(code) === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">SECS-II Data Types</h1>
        <p className="text-[var(--color-text-secondary)]">
          Complete reference for all SECS-II data format codes and their specifications
        </p>
      </div>

      {/* Search and Filter */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        {/* Search Bar */}
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search data types..."
              className="input pl-10"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-tertiary)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-2 md:pb-0">
          {Object.entries(categories).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === key
                  ? 'bg-primary-600 text-white'
                  : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 flex items-center gap-4 text-sm text-[var(--color-text-secondary)]">
        <span>
          Showing <span className="font-semibold text-[var(--color-text-primary)]">{filteredDataTypes.length}</span> of{' '}
          <span className="font-semibold text-[var(--color-text-primary)]">{Object.keys(secsData.dataTypes).length}</span> data types
        </span>
      </div>

      {/* Data Type Grid */}
      {filteredDataTypes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredDataTypes.map(([code, dataType]) => (
            <DataTypeCard key={code} code={code} dataType={dataType} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <svg
            className="mx-auto w-16 h-16 text-[var(--color-text-tertiary)] mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-[var(--color-text-secondary)]">
            No data types found matching your criteria.
          </p>
        </div>
      )}

      {/* Reference Information */}
      <div className="mt-12 card p-6">
        <h2 className="text-xl font-bold mb-4">Data Type Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h3 className="font-semibold mb-2 text-[var(--color-text-primary)]">Structure Types</h3>
            <p className="text-[var(--color-text-secondary)]">
              <span className="font-mono font-semibold">L</span> - List: Ordered collection of heterogeneous items
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2 text-[var(--color-text-primary)]">Text Types</h3>
            <p className="text-[var(--color-text-secondary)]">
              <span className="font-mono font-semibold">A</span> - ASCII: Text string data
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2 text-[var(--color-text-primary)]">Binary Types</h3>
            <p className="text-[var(--color-text-secondary)]">
              <span className="font-mono font-semibold">B</span> - Binary: Raw binary data<br />
              <span className="font-mono font-semibold">BOOLEAN</span> - Boolean: True/False values
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2 text-[var(--color-text-primary)]">Integer Types</h3>
            <p className="text-[var(--color-text-secondary)]">
              <span className="font-mono font-semibold">U1, U2, U4, U8</span> - Unsigned integers (1, 2, 4, 8 bytes)<br />
              <span className="font-mono font-semibold">I1, I2, I4, I8</span> - Signed integers (1, 2, 4, 8 bytes)
            </p>
          </div>
          <div className="md:col-span-2">
            <h3 className="font-semibold mb-2 text-[var(--color-text-primary)]">Floating Point Types</h3>
            <p className="text-[var(--color-text-secondary)]">
              <span className="font-mono font-semibold">F4</span> - Single-precision (IEEE 754, 4 bytes)<br />
              <span className="font-mono font-semibold">F8</span> - Double-precision (IEEE 754, 8 bytes)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
